import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationTypeEnum } from '@edtech-ui/constants/notification.enum';
import { NotificationService } from '@edtech-ui/core/services/notification-managament-services/notification.service';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TeacherNotification } from '@edtech-ui/core/models/notifications/get-teacher-notifications';
import { IstDatePipe } from '@edtech-ui/core/models/Classroom/ist-date.pipe';
import { TimeAgoPipe } from '@edtech-ui/core/pipes/time-ago.pipe';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogBoxComponent } from '../../classroom/delete-dialog-box/delete-dialog-box.component';
import { MarkTeacherNotificationPayload } from '@edtech-ui/core/models/notifications/marked-teacher-notifications';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';

@Component({
	selector: 'app-teachers-notifications',
	standalone: true,
	imports: [MatProgressSpinnerModule, MatIconModule, CommonModule, IstDatePipe, TimeAgoPipe],
	templateUrl: './teachers-notifications.component.html',
	styleUrl: './teachers-notifications.component.scss'
})
export class TeachersNotificationsComponent {

	notificationTypeEnum = NotificationTypeEnum;
	StudyMaterialTypeEnum = StudyMaterialTypeEnum;
	notificationParam!: string

	subscription = new Subscription();
	totalNotificationCount: number = 0;

	isLoading: boolean = true;
	isNotificationOpen = false;

	teacherNotifications: TeacherNotification[] = [];

	@Output() totalCountChanged = new EventEmitter<number>();
	@Output() closeSlider = new EventEmitter<void>();
	@Input() isSlider: boolean = false;
    @Input() source: 'home' | 'notifications' = 'notifications';

	constructor(
		private _notificationService: NotificationService,
		private router: Router,
		private _toastr: ToastrService,
		private dialog: MatDialog,
	) {
		this.subscription.add(this._notificationService.reload$.pipe(tap(() => this.getNotificationsForTeachers())).subscribe());
	}

	ngOnInit(): void {
		this.getNotificationsForTeachers();
	}

	getNotificationsForTeachers(): void {
		this.isLoading = true;
		this.subscription.add(
			this._notificationService.getNotificationsForTeachers().pipe(
				tap(response => {
					if (response.Data && response.Success) {
						this.teacherNotifications = response.Data;
						const unreadNotifications = response.Data.filter((n: any) => !n.is_read);
						this.totalNotificationCount = unreadNotifications.length;
						this.totalCountChanged.emit(this.totalNotificationCount);
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.isLoading = false;
					return of(null);
				})
			).subscribe()
		);
	}

	putMarkedTeacherNotifications(notificationIds: string[]) {
		const unreadNotifications = this.teacherNotifications.filter(
			n => notificationIds.includes(n.id) && !n.is_read);

		if (!unreadNotifications.length) return;

		const payload: MarkTeacherNotificationPayload = {
			notification_ids: unreadNotifications.map(n => n.id)
		};

		this._notificationService.putMarkedTeacherNotifications(payload).pipe(
			tap(response => {
				if (response.Success) {
					unreadNotifications.forEach(n => n.is_read = true);
				}
			}),
			catchError(err => of(null))
		).subscribe();
	}

	markAllNotificationsAsRead() {
		const unreadIds = this.teacherNotifications.filter(n => !n.is_read).map(n => n.id);

		if (!unreadIds.length) return;

		this.putMarkedTeacherNotifications(unreadIds);

		this.totalNotificationCount = 0;
		this.totalCountChanged.emit(this.totalNotificationCount);
	}

	goToClassroomPage(class_id: number) {
		this.router.navigate(['teacher-dashboard/classroom-detail-page', class_id, 'overview']);
	}

	goToQuizPage(class_id: number) {
		this.router.navigate(['teacher-dashboard/classroom-detail-page', class_id, 'quizzes']);
	}

	goToCoursePage(class_id:number){
		this.router.navigate(['teacher-dashboard/classroom-detail-page' ,class_id,'courses']);
	}

	openNotifications() {
		this.isNotificationOpen = !this.isNotificationOpen;
	}

	close() {
		if (this.isSlider) {
			this.closeSlider.emit();
		} else {
			this.router.navigate(['/teacher-dashboard/teacher-dashboard-content']);
		}
	}

	deleteNotificationTeacher(notificationId: string) {
		this.subscription.add(
			this._notificationService.deleteNotificationTeacher(notificationId).subscribe({
				next: () => {
					this._toastr.success('Notification deleted successfully');
					this.getNotificationsForTeachers();
				},
				error: (err) => {
					this._toastr.error('Failed to delete notification');
				}
			})
		);
	}

	openDeleteNotificationBox(notificationId: string) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { notificationId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.deleteNotificationTeacher(notificationId);
			}
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}